/*
 * Deals with an item that's being draggable. It supports SVG
 * and regular HTML elements. SVGs must be wrapped in a container
 * that will be dragged, since they can't alone be dragged.
 */

import {DirectiveBinding} from "vue/types/options";
import {VNode} from "vue";
import {writeData} from "./data";

let cloneNode: Element | null = null
let isDragging: boolean = false

/**
 * A single drag container registration. Keeps track the function to retrieve
 * the data, if we should update the CSS, and the vNode where it belongs.
 */
class DragContainer {
    private css: boolean
    private vNode: VNode
    private data: Function | any

    constructor(vNode: VNode,
                directiveBinding: DirectiveBinding) {
        this.css = directiveBinding.modifiers.css
        this.vNode = vNode
        this.data = directiveBinding.value
    }

    /**
     * Native event called when dragging is started. It is registered by the
     * `registerDraggable` function.
     */
    dragStart = (ev: DragEvent) => {
        let data = this.data
        
        // we need to stop the propagation, otherwise in the case of a tree all
        // the parent nodes will magically have their drop start firing as well
        ev.stopPropagation()

        // if the data is a function, we can't serialize it, and it means the
        // user wants the data returned by this function.
        if (typeof this.data == "function") {
            data = this.data()
        }

        writeData(data, <DataTransfer>ev.dataTransfer)

        // if we need to update the CSS, then we ensure this happens
        if (this.css) {
            (<Element>this.vNode.elm).classList.add("dnd-drag-active")
        }
        
        // FIXME: terrible hack. For some reason the vNode we're having is not the
        //        correct one?!
        if (this.vNode.parent?.data?.on?.dnd_preview_element) {
            cloneNode = (this.vNode.parent?.data?.on?.dnd_preview_element as Function)() as Element
            cloneNode.setAttribute("style", "display: inline-block")
            document.body.append(cloneNode)
            ev.dataTransfer?.setDragImage(cloneNode, 0, 0)
        }
    }

    drag = (ev: DragEvent) => {
        const callback = <Function>this.vNode.data?.on?.dnd_drag
        callback(ev)
    }

    dragEnd = (ev: DragEvent) => {
        const callback = <Function>this.vNode.data?.on?.dnd_dragend

        cloneNode?.parentElement?.removeChild(cloneNode)
        cloneNode = null

        if (this.css) {
            (<Element>this.vNode.elm).classList.remove("dnd-drag-active")
        }

        if (callback) {
            callback(ev)
        }
    }
}


export function bindDraggable(element: HTMLElement,
                              binding: DirectiveBinding,
                              vnode: VNode,
                              oldVnode: VNode) {
    if (!element.hasAttribute("draggable")) {
        element.setAttribute("draggable", "true")
    }
}


export function registerDraggable(element: HTMLElement,
                                  binding: DirectiveBinding,
                                  vnode: VNode,
                                  oldVnode: VNode) {

    const dragContainer = new DragContainer(vnode, binding)

    if (!element.hasAttribute("draggable")) {
        element.setAttribute("draggable", "true")
    }

    element.addEventListener("dragstart", dragContainer.dragStart)

    if (vnode.data?.on?.dnd_drag) {
        element.addEventListener("drag", dragContainer.drag)
    }

    if (vnode.data?.on?.dnd_dragend || binding.modifiers.css) {
        element.addEventListener("dragend", dragContainer.dragEnd)
    }
}
